'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { CircularProgress, Box } from '@mui/material';
import AccessErrorPage from './AccessErrorPage';
import { useAuthStore } from '@/stores/authStore';
import { log } from '@/lib/utils/logger';
import { ApiException } from '@/api/AuthClient';
import { useConfigStore } from '@/stores/configStore';
import { initiateAnonymousSignIn } from '@/lib/auth/signInFlow';

export default function signedInOrAnonymousAllowed<P extends object>(WrappedComponent: React.ComponentType<P>) {
  return function ProtectedRoute(props: P) {
    const router = useRouter();
    const { loginRedirect, isInProgress, currentUser, privileges } = useAuthStore();
    const { systemConfig } = useConfigStore();
    const searchParams = useSearchParams();
    const state = searchParams.get('state');
    const [error, setError] = useState<{ status?: number; message?: string }>();

    useEffect(() => {
      const initiateLogin = async () => {
        if (!isInProgress && !currentUser?.eppn) {
          try {
            let returnAfterLogin: string | undefined;
            if (state) {
              try {
                const decoded = decodeURIComponent(state);
                if (decoded.startsWith('/')) {
                  returnAfterLogin = decoded;
                }
              } catch {
                /* ignore malformed state */
              }
            }
            await initiateAnonymousSignIn(
              systemConfig,
              { loginRedirect },
              returnAfterLogin ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
              router
            );
          } catch (error) {
            log.error('[signedInOrAnonymousAllowed] Login redirect failed:', error);

            if (error instanceof ApiException) {
              setError({
                status: error.status,
                message: error.message || 'Access denied',
              });
            } else if (error instanceof Error) {
              setError({
                message: error.message || 'An unexpected error occurred',
              });
            } else {
              setError({
                message: 'An unexpected error occurred',
              });
            }
          }
        }
      };
      if (systemConfig && !systemConfig.allowAnonymous) {
        initiateLogin();
      }
    }, [systemConfig, currentUser?.eppn, isInProgress, loginRedirect, router, state, privileges]);

    // Show loading spinner while authenticating
    if (isInProgress) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 96px)' }}>
          <CircularProgress />
        </Box>
      );
    }

    // Check authorization for authenticated users
    if (error) {
      return <AccessErrorPage error={error} />;
    }
    return <WrappedComponent {...props} />;
  };
}
